import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthPageShell } from '../components/auth/AuthPageShell';
import Input from '../components/ui/Input';
import { post } from '../shared/apiClient';
import {
  ACTION_COL_CLASS,
  labelClassAuth,
  primaryCtaClassName,
  REGISTER_CARD_CLASS,
} from './register/registerShared';

const ForgotPasswordPage: React.FC = () => {
  const { t } = useTranslation(['auth', 'errors']);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!email.trim()) return;
      setError(null);
      setSubmitting(true);
      try {
        await post('/auth/forgot-password', { email: email.trim() });
        setSent(true);
      } catch {
        setError(t('errors:generic'));
      } finally {
        setSubmitting(false);
      }
    },
    [email, t],
  );

  return (
    <AuthPageShell>
      <div className={REGISTER_CARD_CLASS}>
        <h1 className="text-center text-[22px] font-semibold leading-tight text-gray-900">
          {t('auth:forgot_password.title')}
        </h1>

        {sent ? (
          <div className="flex flex-col gap-4 text-center">
            <p className="text-sm text-gray-600">{t('auth:forgot_password.sent_message')}</p>
            <Link to="/signin" className="text-sm font-semibold text-gray-900 underline">
              {t('auth:forgot_password.back_to_sign_in')}
            </Link>
          </div>
        ) : (
          <form className="flex flex-col gap-6" onSubmit={handleSubmit} noValidate>
            <Input
              id="forgot-email"
              name="email"
              type="email"
              autoComplete="email"
              label={t('auth:forgot_password.enter_email')}
              labelClassName={labelClassAuth}
              placeholder={t('auth:shared.email_placeholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="!focus:border-[#6ECFCF] !focus:ring-[#6ECFCF]"
            />
            {error ? (
              <p className="text-sm text-red-600" role="alert">
                {error}
              </p>
            ) : null}
            <div className={ACTION_COL_CLASS}>
              <button
                type="submit"
                disabled={!email.trim() || submitting}
                className={primaryCtaClassName + ' disabled:opacity-50'}
              >
                {submitting ? t('auth:forgot_password.sending') : t('auth:forgot_password.send_link')}
              </button>
              <Link to="/signin" className="text-center text-sm text-gray-600 hover:underline">
                {t('auth:forgot_password.back_to_sign_in')}
              </Link>
            </div>
          </form>
        )}
      </div>
    </AuthPageShell>
  );
};

export default ForgotPasswordPage;
